import { useState } from "react";

/*
 * useMultiStepForm is a convenience for tracking progress through a multistep form.
 * The hook tracks steps internally and exposes optional callbacks:
 *  onBack:  Called when prevStep triggers
 *  onNext:  Called when nextStep triggers
 *  onFirst: Called when the initial step is reached.
 *    Not the first time the initial step is rendered.
 *    Useful for "backing out" of a form.
 *  onLast:  Called when the final step is reached.
 */
export const useMultiStepForm = ({
  initialStep,
  finalStep,
  onBack,
  onNext,
  onFirst,
  onLast,
}: {
  initialStep: number;
  finalStep: number;
  onBack?: () => void;
  onNext?: () => void;
  onFirst?: () => void;
  onLast?: () => void;
}) => {
  const [currentStep, setCurrentStep] = useState(initialStep);

  const nextStep = () => {
    setCurrentStep((s) => Math.min(s + 1, finalStep));
    onNext?.();
    if (currentStep === finalStep) {
      onLast?.();
    }
  };

  const prevStep = () => {
    setCurrentStep((s) => Math.max(s - 1, initialStep));
    onBack?.();
    if (currentStep === initialStep) {
      onFirst?.();
    }
  };

  return {
    currentStep,
    isFirstStep: currentStep === initialStep,
    isLastStep: currentStep === finalStep,
    nextStep,
    prevStep,
  };
};
